import CachedIcon from "@mui/icons-material/Cached";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  Backdrop,
  Box,
  Button,
  Fade,
  IconButton,
  Modal,
  Typography,
} from "@mui/material";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_Row,
  MRT_TableInstance,
  useMaterialReactTable,
} from "material-react-table";
import { useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../app/features/store/store";
import {
  DdRouteProgressDto,
  RouteIntervalProgress,
  RtsDetails,
} from "../../app/models/responseModels/DdRouteProgressResponse";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";
import ProgressBar from "../../helpers/ProgressBar";
import { getDefaultMRTOptions } from "../dedicatedDelivery/DefaultMrtOptions";
import RouteProgressChart from "./RouteProgressChart";
import RtsDetailsModal from "./models/RtsDetailsModal";
import { axiosRequests } from "../../app/api/axiosRequest";
import { debug } from "console";
import { number } from "yup";
import { updateRouteProgressEstimatedTripTime, updateRouteProgressOrdersUndelivered  } from "../../app/features/dedicatedDelivery/routeSlice";
import { toast } from "react-toastify";
import getAxiosError from "../../app/api/axiosError";

interface RouteProgressDataProps {
  data: DdRouteProgressDto[];
  loading: boolean;
  refresh: CallableFunction;
  handelExport: CallableFunction;
}
const RouteProgressData: React.FC<RouteProgressDataProps> = ({
  data,
  loading,
  refresh,
  handelExport,
}) => {
  const defaultMRTOptions = getDefaultMRTOptions<DdRouteProgressDto>();

  const [openChartModal, setOpenChartModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<
    RouteIntervalProgress[]
  >([]);

  const inputRef = useRef(null);

  const isFdc = useSelector(
    (state: RootState) => state.login?.authResponse?.isFdc
  );

  // Custom debounce function in TypeScript
  function debounce<T extends (...args: any[]) => void>(
    func: T,
    delay: number
  ): (...args: Parameters<T>) => void {
    let timeout: ReturnType<typeof setTimeout>; // Correct type for `setTimeout` return
    return (...args: Parameters<T>) => {
      clearTimeout(timeout); // Clears the timeout if the function is called again
      timeout = setTimeout(() => func(...args), delay); // Sets the new timeout
    };
  }

  // Example usage
  const debouncedSave = debounce((id: string, value: string) => {
    saveEstimatedTripTime(id, value);
  }, 300);

  const debouncedUpdateOrdersUndelivered = debounce((routeId: string, value: number) => {
    saveOrdersUndelivered(routeId, value);
  }, 300);
  
  
  

  const [openRtsModal, setOpenRtsModal] = useState(false);
  const [rtsDetailsList, setRtsDetailsList] = useState<RtsDetails[]>([]);

  const handleOpenChartModal = (row: DdRouteProgressDto) => {
    setSelectedRowData(row.routeIntervalProgress);
    setOpenChartModal(true);
  };

  const handleCloseChartModal = () => {
    setOpenChartModal(false);
    setSelectedRowData([]);
  };

  const dispatch = useDispatch();

  const saveEstimatedTripTime = async (
    id: string,
    estimatedTripTime: string
  ) => {
    const url = `/fdc-commandops/dedicated-delivery/estimated-trip-time/${id}`;
    try {
      const response = await axiosRequests.postWithQueryParamAndReturnBlob(
        url,
        {},
        { params: { estimatedTripTime: estimatedTripTime } }
      );
      // Dispatch the action to update the estimatedTripTime for the specific id
      dispatch(
        updateRouteProgressEstimatedTripTime({
          id,
          estimatedTripTime: estimatedTripTime,
        })
      );
      toast.success("Estimated Time Updated Successfully", {
        toastId: "estimatedTimeUpdate",
      });
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "estimatedTimeUpdateFailure",
      });
    }
  };

  const saveOrdersUndelivered = async (routeId: string, ordersUndeliveredUnattempted: number) => {
    const url = `/fdc-commandops/dedicated-delivery/orders-undelivered-no-attempt/${routeId}`;
    try {
      const response = await axiosRequests.postWithQueryParamAndReturnBlob(
        url,
        {},
        { params: { ordersUndeliveredNoAttempt: ordersUndeliveredUnattempted } }
      );
  
      // Dispatch the action to update the Redux store
      dispatch(updateRouteProgressOrdersUndelivered({ id: routeId, ordersUndeliveredUnattempted}));
  
      toast.success("Orders Undelivered Updated Successfully", {
        toastId: "ordersUndeliveredUpdate",
      });
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "ordersUndeliveredUpdateFailure",
      });
    }
  };
  
  
  

  // Function to handle opening the modal with the idle time data
  const handleRtsDetailsModal = (row: RtsDetails[]) => {
    if (row) {
      setRtsDetailsList(row);
      setOpenRtsModal(true);
    }
  };

  // Function to handle closing the modal
  const handleCloseRtsDetailsModal = () => {
    setOpenRtsModal(false);
    setRtsDetailsList([]);
  };

  // Columns to be used on grid on landing page
  const columns = useMemo<MRT_ColumnDef<DdRouteProgressDto>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        size: 10,
        enableEditing: false,
      },
      {
        accessorKey: "driverName",
        header: "Driver Name",
        size: 10,
        enableEditing: false,
      },
      {
        accessorKey: "ROUTE_STATUS",
        header: "Route Status",
        enableEditing: false,
        size: 10,
        Cell: ({ cell, row }) =>
          row.original.ROUTE_STATUS === "Picked Up"
            ? "In Progress"
            : row.original.ROUTE_STATUS,
      },
      {
        accessorKey: "PROGRESS",
        header: "Current Progress",
        enableEditing: false,
        size: 10,
        Cell: ({ cell, row }) => (
          <ProgressBar
            current={
              (row.original.DELIVERED_ORDERS ?? 0) +
              (row.original.RETURNED_ORDERS ?? 0)
            }
            total={row.original.CURRENT_ORDERS_ON_BUNDLE ?? 0}
          />
        ),
        sortingFn: (a, b) => {
          // Calculate progress percentages for sorting
          const aProgress =
            ((a.original.DELIVERED_ORDERS ?? 0) +
              (a.original.RETURNED_ORDERS ?? 0)) /
            (a.original.CURRENT_ORDERS_ON_BUNDLE ?? 1);

          const bProgress =
            ((b.original.DELIVERED_ORDERS ?? 0) +
              (b.original.RETURNED_ORDERS ?? 0)) /
            (b.original.CURRENT_ORDERS_ON_BUNDLE ?? 1);

          return aProgress - bProgress;
        },
      },
      {
        accessorKey: "associatedFdpName",
        header: "Associated FDP Name",
        enableEditing: false,
        size: 10,
      },
      {
        accessorKey: "ROUTE_ID",
        header: "Route ID",
        size: 10,
        enableEditing: false,
      },
      {
        header: "Total Package(s)",
        size: 10,
        accessorKey: "CURRENT_ORDERS_ON_BUNDLE",
        enableEditing: false,
      },

      /* {
        header: "Estimated Route Duration",  
        size: 10,
      }, */
      {
        accessorKey: "stopsInLastOneHour",
        header: "Package(s) In Last 1 Hour",
        enableEditing: false,
        size: 10,
        Cell: ({ row }) => (
          <Box display="flex" alignItems="center" gap={2}>
            <span>{row.original.stopsInLastOneHour}</span>
            <Button
              color="primary"
              variant="outlined"
              onClick={() => {
                handleOpenChartModal(row.original);
              }}
            >
              Track Me
            </Button>
          </Box>
        ),
      },
      {
        accessorKey: "ARRIVED_AT",
        header: "Driver Arrival Time",
        enableEditing: false,
        size: 10,
        Cell: ({ cell, row }) => (
          <div>
            {row.original?.ARRIVED_AT ? (
              <span>
                {new Date(cell.getValue<string>()).toLocaleString("en-US", {
                  hourCycle: "h12",
                })}
              </span>
            ) : (
              <span></span>
            )}
          </div>
        ),
      },
      /* {
        accessorKey: "averageStopsInLastOneHour",
        header: "Average Stops Per 1 Hour",
        size: 10,
      }, */
      /* {
        header: "Load Out Time",
        size: 10,
      }, */
      {
        accessorKey: "ARRIVAL_WINDOW",
        header: "Loadout Start time",
        size: 10,
        enableEditing: false,
        Cell: ({ cell, row }) => (
          <div>
            {row.original.ARRIVAL_WINDOW ? (
              <span>
                {new Date(cell.getValue<string>()).toLocaleString("en-US", {
                  hourCycle: "h12",
                })}
              </span>
            ) : (
              <span></span>
            )}
          </div>
        ),
      },
      {
        header: "Route Start Time",
        size: 10,
        accessorKey: "STARTED_AT",
        enableEditing: false,
        Cell: ({ cell, row }) => (
          <div>
            {row.original.STARTED_AT ? (
              <span>
                {new Date(cell.getValue<string>()).toLocaleString("en-US", {
                  hourCycle: "h12",
                })}
              </span>
            ) : (
              <span></span>
            )}
          </div>
        ),
      },
      /*{
        header: "Time On Route",
        size: 10,
      }, */
      {
        header: "Route Completion",
        size: 10,
        accessorKey: "COMPLETED_AT",
        enableEditing: false,
        Cell: ({ cell, row }) => (
          <div>
            {row.original.COMPLETED_AT ? (
              <span>
                {new Date(cell.getValue<string>()).toLocaleString("en-US", {
                  hourCycle: "h12",
                })}
              </span>
            ) : (
              <span></span>
            )}
          </div>
        ),
      },
      {
        header: "Returned Order(s)",
        size: 10,
        accessorKey: "RETURNED_ORDERS",
        enableEditing: false,
        Cell: ({ cell, row }) =>
          !row.original.RETURNED_ORDERS || row.original.RETURNED_ORDERS == 0
            ? ""
            : row.original.RETURNED_ORDERS,
      },
      {
        accessorKey: "ordersUndeliveredUnattempted",
        header: "Orders - Undelivered - No Attempt",
        size: 10,
        muiEditTextFieldProps: ({ cell, row, table }) => ({
          inputProps: {
            type: "number", 
            placeholder: "Enter a number", 
            min: 0, 
          },
          onKeyDown: (event) => {
            if (event.keyCode === 13) {
              const inputValue = (event.target as HTMLInputElement).value.trim();
              // Validate the input to allow only integers
              const isValid = /^[0-9]+$/.test(inputValue) && Number(inputValue) >= 0 && Number(inputValue) <= 999;
      
              if (isValid) {
                debouncedUpdateOrdersUndelivered(row?.original?.ROUTE_ID ?? 0, parseInt(inputValue, 10));
              } else {
                toast.error("Please enter a valid integer value", {
                  toastId: "integerValidationError",
                });
              }
            }
          },
        }),
      }
      ,
      
      {
        header: "Total RTS Package(s)",
        size: 10,
        accessorKey: "totalRtsPackages",
        enableEditing: false,
        Cell: ({ row }) =>
          row?.original?.totalRtsPackages && (
            <Box>
              <span>{row?.original?.totalRtsPackages}</span>
              <Button
                color="primary"
                variant="outlined"
                sx={{
                  bgcolor: "lightgreen", // Set the background color to light green
                  color: "black", // Set the text color to black
                  "&:hover": {
                    bgcolor: "green", // Change background color on hover
                    color: "white", // Change text color on hover
                  },
                  marginLeft: "10px",
                }}
                onClick={() => {
                  handleRtsDetailsModal(
                    row?.original?.rtsPackageSummaryDetails?.rtsDetails || []
                  );
                }}
              >
                Load More
              </Button>
            </Box>
          ),
      },
      {
        header: "Last Delivery At",
        size: 10,
        accessorKey: "LAST_DELIVERY_AT",
        enableEditing: false,
        Cell: ({ cell, row }) => (
          <div>
            {row.original.LAST_DELIVERY_AT ? (
              <span>
                {new Date(cell.getValue<string>()).toLocaleString("en-US", {
                  hourCycle: "h12",
                })}
              </span>
            ) : (
              <span></span>
            )}
          </div>
        ),
      },
      {
        accessorKey: "totalTripTimes",
        header: "Total Trip Time",
        enableEditing: false,
        size: 10,
        Cell: ({ cell, row }) => (
          <div>
            {row.original?.totalTripTimes ? (
              <span>{row?.original?.totalTripTimes}</span>
            ) : (
              <span></span>
            )}
          </div>
        ),
      },
      {
        accessorKey: "estimatedTripTime",
        header: "Estimated Trip Time",
        size: 10,
        muiEditTextFieldProps: ({ cell, row, table }) => ({
          inputProps: {
            type: "text",
            placeholder: "HH:mm", // Add placeholder text here
            min: 0, // Optional: to restrict negative numbers
          },
          onKeyDown: (event) => {
            if (event.keyCode === 13) {
              const inputValue = (event.target as HTMLInputElement).value;
              // Validate the input here (e.g., using a regular expression)
              const isValid = /^([01]\d|2[0-3]):[0-5]\d$/.test(inputValue);
              if (isValid) {
                debouncedSave(row?.original?.ROUTE_ID ?? 0, inputValue.trim());
              } else {
                toast.error("Please Enter the value in the HH:mm format ", {
                  toastId: "estimatedTripTimeFormat",
                });
              }
            }
          },
          onBlur: (event) => {
            table.setEditingCell(null); // Exit editing mode when input loses focus
          },
        }),
      },
      {
        header: "Last Update At",
        size: 10,
        accessorKey: "fileLastUpdateAt",
        enableEditing: false,
        Cell: ({ cell, row }) => (
          <div>
            {row.original.fileLastUpdateAt ? (
              <span>
                {new Date(cell.getValue<string>()).toLocaleString("en-US", {
                  hourCycle: "h12",
                })}
              </span>
            ) : (
              <span></span>
            )}
          </div>
        ),
      },
    ],
    []
  );

  // authenticated user information from store
  const { authResponse } = useSelector((state: RootState) => state.login);

  // elements to be rendered on top of the grid
  const renderTopToolbarCustomActions = (
    table: MRT_TableInstance<DdRouteProgressDto>
  ) => {
    return (
      <Box>
        <CustomTooltip arrow title="Refresh" onClick={() => refresh()}>
          <IconButton type="button">
            <CachedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip arrow title="Export" onClick={() => handelExport()}>
          <IconButton type="button">
            <FileDownloadOutlinedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
      </Box>
    );
  };

  // preprare MRT V2 grid
  const table = useMaterialReactTable({
    ...defaultMRTOptions,
    columns: columns,
    enableSorting: true,
    data: data,
    initialState: {
      ...defaultMRTOptions.initialState,
      columnVisibility: {
        id: false,
        totalTripTimes: isFdc ? true : false,
        estimatedTripTime: isFdc ? true : false,
        ordersUndeliveredUnattempted: isFdc ? true : false,
      },
    },
    state: {
      showProgressBars: loading,
    },
    enableEditing: true,
    editDisplayMode: "cell",
    //optionally, use single-click to activate editing mode instead of default double-click
    muiTableBodyCellProps: ({ cell, column, table }) => ({
      onClick: () => {
        table.setEditingCell(cell); //set editing cell
        //optionally, focus the text field
        queueMicrotask(() => {
          const textField = table.refs.editInputRefs.current[column.id];
          if (textField) {
            textField.focus();
            textField.select?.();
          }
        });
      },
    }),

    muiTableBodyRowProps: ({ row }) => ({
      onClick: (e) => {},
      ...defaultMRTOptions.muiTableBodyRowProps,
      sx: {
        cursor: "pointer",
      },
    }),
    renderTopToolbarCustomActions: ({ table }) =>
      renderTopToolbarCustomActions(table),
    renderBottomToolbarCustomActions: () => {
      return (
        <Typography variant="subtitle2" component="p">
          Total Rows: {data.length}
        </Typography>
      );
    },
  });

  return (
    <>
      <MaterialReactTable table={table} />
      <Modal
        open={openChartModal}
        onClose={handleCloseChartModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={openChartModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 800,
              bgcolor: "background.paper",
              border: "2px solid #000",
              boxShadow: 24,
              p: 4,
            }}
          >
            <Typography variant="h6" component="h2">
              Route Progress Chart
            </Typography>
            {selectedRowData && (
              <>
                <RouteProgressChart data={selectedRowData} />
                <Box mt={3}>
                  <Typography variant="body1">
                    <strong>Total Packages Delivered:</strong>{" "}
                    {selectedRowData.reduce(
                      (acc, row) => acc + row.packagesDeliveredInLast30Min,
                      0
                    )}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Fade>
      </Modal>
      <Modal
        open={openRtsModal}
        onClose={handleCloseRtsDetailsModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 800,
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
          }}
        >
          <RtsDetailsModal data={rtsDetailsList} />
        </Box>
      </Modal>
    </>
  );
};

export default RouteProgressData;
